import { DocumentRegular } from '@fluentui/react-icons';
import { get } from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import { COURSE_MATERIAL_FOLDER_PREFIX, FileItem } from '@mnspro-cloud/services';
import { FolderIcon } from './../../Icons/FolderIcon';
import { getFileIcon } from '../../FileUtils/getFileIcon';

/**
 * Renders the mimetype icon of the given FileItem
 */
export const getFileListRowIcon = (file: FileItem) => {
  if (file.folder !== undefined && file.folder !== null) {
    return <FolderIcon style={{ width: '16px' }} />;
  }

  if (file.file !== undefined && file.file !== null && file.file.mimeType) {
    return getFileIcon(file.file.mimeType);
  }

  return <DocumentRegular />;
};

/**
 * Returns true, if the given file has been created within
 * the last 24 hours.
 */
export const isRecentFile = (file: FileItem): boolean => {
  const createdDateTime = get(file, 'createdDateTime', false);

  if (createdDateTime === false) {
    return false;
  }

  const duration = moment.duration(moment().diff(moment(createdDateTime)));
  const hours = duration.asHours();

  return hours <= 24;
};

export const isFolder = (file: FileItem): boolean => {
  return get(file, 'folder', false) !== false;
};

export const canBeOpened = (file: FileItem): boolean =>
  get(file, 'webUrl', false) !== false;

export const isSelectable = (
  file: FileItem,
  allowFolderSelection: boolean,
  allowFileSelection: boolean,
): boolean => (file.folder ? allowFolderSelection : allowFileSelection);

export type SelectAllState = boolean | 'mixed';

export const getSelectAllState = (
  selectable: FileItem[],
  selected: ReadonlySet<FileItem>,
): SelectAllState => {
  const selectedCount = selectable.filter((file) => selected.has(file)).length;

  if (selectedCount === 0) {
    return false;
  }

  return selectedCount === selectable.length ? true : 'mixed';
};

export type DownloadMode = 'file' | 'zip';

/**
 * Returns true, if the given items can be downloaded together as one ZIP:
 * all from one drive and none of them the synthetic course material folder.
 */
export const canDownloadAsZip = (selected: FileItem[]): boolean => {
  if (selected.length === 0) {
    return false;
  }

  const driveId = selected[0].parentReference?.driveId;

  return selected.every(
    (item) =>
      item.parentReference?.driveId === driveId &&
      !item.id?.startsWith(COURSE_MATERIAL_FOLDER_PREFIX),
  );
};

/**
 * Returns how the given selection can be downloaded: a single file directly,
 * a folder or several items as ZIP archive (only when all items share one drive),
 * or `null` when the selection cannot be downloaded at all.
 */
export const getDownloadMode = (
  selected: FileItem[],
  canDownloadFile: boolean,
  canDownloadZip: boolean,
): DownloadMode | null => {
  if (selected.length === 0) {
    return null;
  }

  if (selected.length === 1 && !isFolder(selected[0])) {
    return canDownloadFile ? 'file' : null;
  }

  return canDownloadZip && canDownloadAsZip(selected) ? 'zip' : null;
};

/**
 * Returns true, if the given file can be converted to pdf
 */
export const isDownloadableAsPdf = (file: FileItem): boolean => {
  const mimeType = get(file, 'file.mimeType', '') as string;

  switch (mimeType) {
    case 'application/msword':
    case 'aplication/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.template':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return true;
    default:
      return false;
  }
};
